import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import { PatientReimbursement } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { Modal, ModalFooterItemConfiguration, ModalWidth, StandardSpinner, StyledFlex } from '@dreryk/react-components';

import { translationKeys } from '../../translations';
import { MedicineActiveIngredients } from '../MedicineActiveIngredients';
import { MedicineName } from '../MedicineName';
import { MedicinePackage } from '../MedicinePackage';
import { MedicineReimbursementSelector } from '../MedicineReimbursementSelector/MedicineReimbursementSelector';
import { OnReimbursementClick } from '../Reimbursement/Reimbursement.types';

import { MedicineDetailsAccordion } from './MedicineDetailsAccordion/MedicineDetailsAccordion';
import { MedicineDetailsMarketStatus } from './MedicineDetailsMarketStatus';
import { useMedicineDetailsQuery } from './MedicineDetailsModal.hooks';
import { StyledMedicineDetailsModalContent } from './MedicineDetailsModal.styled';

type MedicineDetailsModalPropsWithReimbursements = {
   reimbursements: PatientReimbursement[];
   visitDiagnoses: VisitDiagnosis[];
};

type MedicineDetailsModalPropsWithoutReimbursements = {
   reimbursements?: never;
   visitDiagnoses?: never;
};

type MedicineDetailsModalProps = {
   readonly footerItems: ModalFooterItemConfiguration[];
   readonly onCancel: () => void;
   readonly medicine: Omit<MedicineSimple, 'narcoticOrPsychotropic' | 'inPersonExaminationRequired'>;
   readonly onReimbursementClick?: OnReimbursementClick;
} & (MedicineDetailsModalPropsWithReimbursements | MedicineDetailsModalPropsWithoutReimbursements);

const MedicineDetailsModal = (props: MedicineDetailsModalProps): JSX.Element => {
   const { data: medicineDetails, loading: medicalDetailsLoading } = useMedicineDetailsQuery(props.medicine.id);

   return (
      <Modal
         title={translationKeys.medicine.details.medicineDescription}
         footerItems={props.footerItems}
         onCloseRequest={props.onCancel}
         cancelButtonLabel={'common:cancel'}
         onCancel={props.onCancel}
         width={ModalWidth.WIDE}
         testId={'medicineDetails'}
      >
         <StyledMedicineDetailsModalContent>
            <MedicineName name={props.medicine.fullName} atc={props.medicine.atc} />
            <MedicineActiveIngredients activeIngredients={props.medicine.activeIngredients} />
            <MedicinePackage
               form={props.medicine.form}
               dose={props.medicine.dose}
               packageContent={props.medicine.packageContent}
            />
            <StyledFlex alignItems={'center'} gap={'10px'}>
               {props.reimbursements ? (
                  <MedicineReimbursementSelector
                     reimbursements={props.reimbursements}
                     onReimbursementClick={props.onReimbursementClick}
                     disabled={false}
                     visitDiagnoses={props.visitDiagnoses}
                  />
               ) : null}
               {medicineDetails ? <MedicineDetailsMarketStatus medicineDetails={medicineDetails} /> : null}
            </StyledFlex>
            {medicineDetails ? (
               <>
                  <MedicineDetailsAccordion medicineDetails={medicineDetails} />
               </>
            ) : (
               medicalDetailsLoading && (
                  <StandardSpinner text={translationKeys.medicine.details.loadingMedicineDescription} />
               )
            )}
         </StyledMedicineDetailsModalContent>
      </Modal>
   );
};

export { MedicineDetailsModal };
